import React, { useState, useEffect } from 'react';
import { withStyles } from '@material-ui/core';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styles from '~/pages/Loads/styles';
import {
  getMaterials,
  getLoadFactors,
  setLoadFactorValues,
  updateLoadFactors,
  addLoadFactor,
  resetLoadFactors,
  getElements,
  getSubElements,
} from '~/store/manager/actions';
import { parseDate } from '~/utils/moment';

import PF2MDataTable from '~/components/PF2MDataTable';

const LoadFactorTab = ({ classes }) => {
  const [dataLoaded, setDataLoaded] = useState(false);
  const dispatch = useDispatch();
  const materials = useSelector(state => state.manager.materials);
  const loadFactors = useSelector(state => state.manager.loadFactors);
  const elements = useSelector(state => state.manager.elements);
  const subElements = useSelector(state => state.manager.subElements);
  const { t: translate } = useTranslation();
  const activeStatus = [
    { name: translate('common:Active'), value: 1 },
    { name: translate('common:Inactive'), value: 0 },
  ];

  useEffect(() => {
    async function fetchData() {
      dispatch(getLoadFactors());
      dispatch(getMaterials());
      dispatch(getElements());
      dispatch(getSubElements());
    }

    if (!dataLoaded) {
      fetchData();
      setDataLoaded(true);
    }
  }, [dataLoaded, dispatch]);

  const validateLookupField = key => (rowData) => {
    if (['', '0', 0].includes(rowData[key])) {
      return { isValid: false, helperText: translate('validation: RequiredField') };
    }

    return { isValid: true };
  };

  const hasPeriodConflict = (newRow, allRows) => {
    const newStart = parseDate(newRow.start_date, true);
    const newEnd = parseDate(newRow.end_date, true);

    return allRows.some((row) => {
      if (row.id === newRow.id) return false;

      const sameMaterial = row.material_name === newRow.material_name;
      const sameElement = row.element_name === newRow.element_name;
      const sameSubElement = row.element_point_name === newRow.element_point_name;

      if (!sameMaterial || !sameElement || !sameSubElement) {
        return false;
      }

      const rowStart = parseDate(row.start_date, true);
      const rowEnd = parseDate(row.end_date, true);

      return newStart.isSameOrBefore(rowEnd)
        && newEnd.isSameOrAfter(rowStart);
    });
  };

  const validateDate = key => (row) => {
    const startDate = parseDate(row.start_date, true);
    const endDate = parseDate(row.end_date, true);

    if (key === 'end_date' && startDate.isAfter(endDate)) {
      return {
        isValid: false,
        helperText: translate('validation:FinalDateSmallerThanInitialDate'),
      };
    }

    if (hasPeriodConflict(row, loadFactors)) {
      return {
        isValid: false,
        helperText: translate('validation:ConflictingLoadFactorPeriod'),
      };
    }

    return { isValid: true };
  };

  const columns = [
    {
      field: 'start_date',
      customType: 'dateFuture',
      title: translate('common:StartDate'),
      editable: 'always',
      disableFuture: false,
      disablePast: false,
    },
    {
      field: 'end_date',
      customType: 'dateFuture',
      title: translate('common:EndDate'),
      editable: 'always',
      validate: validateDate('end_date'),
      disableFuture: false,
      disablePast: false,
    },
    {
      editable: 'always',
      title: translate('common:Element'),
      field: 'element_name',
      required: true,
      lookup: elements.map(item => ({ ...item, id: item.name })),
      customType: 'filterLookup',
      validate: validateLookupField('element_name'),
    },
    {
      field: 'element_point_name',
      title: translate('common:SubElement'),
      customType: 'filterLookup',
      validate: validateLookupField('element_point_name'),
      lookup: subElements.map(item => ({ ...item, id: item.name })),
      lookupFilter: (option, row) => String(option.element_name) === String(row.element_name)
        || String(row.element_name) === '0',
      editable: 'always',
      required: true,
    },
    {
      field: 'material_name',
      title: translate('common:Material'),
      customType: 'filterLookup',
      validate: validateLookupField('material_name'),
      lookup: materials.map(item => ({ ...item, id: item.name })),
      editable: 'always',
      required: true,
    },
    {
      field: 'value',
      title: translate('common:LoadFactor'),
      editable: 'always',
      type: 'decimal',
      decimalPrecision: 6,
      required: true,
    },
    {
      field: 'active',
      title: 'STATUS',
      align: 'left',
      editable: 'always',
      customType: 'filterLookup',
      lookup: activeStatus.map(item => ({ ...item, id: item.value })),
    },
  ];

  return (
    <div className={classes.tabContainer}>
      <PF2MDataTable
        title={translate('common:LoadFactor')}
        options={{
          showTitle: true,
          search: false,
          selection: true,
          filtering: true,
          pageSize: 20,
          pageSizeOptions: [20, 50, 100],
          editableRowValidation: rowData => (rowData.id < 0 && rowData.update_status !== 'I'),
          maxBodyHeight: 'calc(100vh - 350px)',
          minBodyHeight: 'calc(100vh - 350px)',
          hideDelete: true,
        }}
        onAdd={newData => dispatch(addLoadFactor(newData))}
        onApply={() => dispatch(updateLoadFactors(loadFactors))}
        onDiscard={() => dispatch(resetLoadFactors())}
        onChange={(newData) => {
          newData.forEach((row) => {
            dispatch(setLoadFactorValues({
              ...row,
              material_id: materials.find(m => m.name === row.material_name)?.id,
              element_id: elements.find(e => e.name === row.element_name)?.id,
              element_point_id: subElements.find(se => se.name === row.element_point_name)?.id,
            }));
          });
        }}
        columns={columns}
        data={loadFactors.length > 0 ? loadFactors : []}
      />
    </div>
  );
};

LoadFactorTab.propTypes = {
  classes: PropTypes.object.isRequired,
};

LoadFactorTab.defaultProps = {};

export default withStyles(styles)(LoadFactorTab);
